import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ClarityModule, ClrTab } from '@clr/angular';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { map, pairwise } from 'rxjs/operators';

import { Row, Table } from 'src/app/models/ui/table';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { HideIDColumnsPipe } from 'src/app/pipes/hide-idcolumns.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { MrUnitTextPipe } from 'src/app/pipes/mrUnitText.pipe';
import { TablePrettyPrintPipe } from 'src/app/pipes/tablePrettyPrint.pipe';
import { BasicStoreService } from 'src/app/services/BasicStore/basic-store.service';
import { ColumnFilterComponent } from '../../_components/filter/column-filter/column-filter.component';
import {
  FILTER_TABLE_STORE,
  FILTER_TABLE_STORE_II,
  provideFilterStore,
} from 'src/app/shared/stores/filter-table.store';
import { IconDgCellComponent } from '../../_components/_shared/datagrid-cell/icon-dg-cell.component';
import { Gruppe } from 'src/app/types/api';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormGroupOfType } from 'src/app/types/shared';
import { BegehungApiService } from 'src/app/services/APIService/begehung.api.service';
import { TABLE_EMPTY } from 'src/app/models/ui/table/table';
import { ConfirmService } from 'src/app/services/confirm.service';
import { AllSelectedBtnComponent } from '../../_components/_shared/all-selected-btn/all-selected-btn.component';
import { errorHandle } from 'src/app/misc/functions';

type ZuordnungsMap = Record<number /* obegeid or -1 für freie Objekte */, Row[] /* zugeordnete Anlagen */>;

@Component({
  selector: 'app-begehungseinstellungen',
  templateUrl: './begehungseinstellungen.component.html',
  styleUrls: ['./begehungseinstellungen.component.scss'],
  imports: [
    CommonModule,
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    ColumnFilterComponent,
    IconDgCellComponent,
    AllSelectedBtnComponent,
    MrTranslatePipe,
    BackendLocaleDatePipe,
    MrUnitTextPipe,
    HideIDColumnsPipe,
    TablePrettyPrintPipe,
  ],
  providers: [provideFilterStore({ secondStore: true })],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BegehungseinstellungenComponent {
  private readonly apiService = inject(BegehungApiService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastrService);
  private readonly basicStore = inject(BasicStoreService);
  private readonly confirm = inject(ConfirmService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);
  protected readonly filterStore = inject(FILTER_TABLE_STORE);
  protected readonly filterStore_II = inject(FILTER_TABLE_STORE_II);

  private readonly formTab = viewChild<ClrTab>('form');
  private readonly columnFilters = viewChildren<ColumnFilterComponent>('all');
  private readonly columnFilters_II = viewChildren<ColumnFilterComponent>('zugeordnet');

  public closedialog = new BehaviorSubject(false);
  private dataRefresh: Subject<void>;

  set data(dataIn: any) {
    this.dataRefresh = dataIn.dataRefresh;
  }

  //#region Begehungen
  //#region Form
  protected formGroup = new FormGroupOfType<BegehungsEinstellungen>({
    ID: new FormControl<number>(null),
    Begehungsart: new FormControl('', Validators.required),
    Beginn: new FormControl(BackendLocaleDatePipe.today, { nonNullable: true, validators: Validators.required }),
    Intervall: new FormControl(2, { nonNullable: true, validators: [Validators.required, Validators.min(1)] }),
    Tag: new FormControl(1, { nonNullable: true, validators: Validators.required }),
    Beschreibung: new FormControl(''),
    Gruppen: new FormControl<string>('1', { nonNullable: true, validators: Validators.required }),
  });

  protected get dateControl(): string {
    return this.formGroup.get('Beginn').value;
  }
  protected set dateControl(value: string) {
    this.formGroup.get('Beginn').setValue(value);
  }
  //#endregion

  private readonly tableResource = rxResource<Table<BegehungsEinstellungen>, void>({
    stream: () => this.apiService.getEinstellungenTable(),
    defaultValue: TABLE_EMPTY(),
  });
  protected readonly begehungsTable = computed(() => this.tableResource.value());
  protected readonly selectedBegehung = signal<BegehungsEinstellungen>(null);
  protected readonly obegeid = computed(() => this.selectedBegehung()?.ID);
  //#endregion

  //#region Objekte
  private readonly zuordnungenResource = rxResource<BegehungZuordnung[], void>({
    stream: () => this.apiService.getAllZuordnungen(),
    defaultValue: [],
  });

  private readonly allObjekte = toSignal<Table, Table>(
    this.basicStore.getComponentStoreAsync('anlagen', true).pipe(map(x => x.anlagen as Table)),
    { initialValue: TABLE_EMPTY() }
  );

  protected readonly _zuordnungMap = computed<ZuordnungsMap>(() => {
    const objekte = [...this.allObjekte().rows];
    const zuordnungen = this.zuordnungenResource.value();

    const mapped: ZuordnungsMap = {};

    for (const { OBEGEID, OSTAMMID } of zuordnungen) {
      const ind = objekte.findIndex(row => row.OSTAMMID == OSTAMMID);
      if (ind == -1) /* zuordnung ist nicht in Anlagen (groups?) */ continue;
      const objekt = objekte[ind];
      //objekte.splice(ind, 1);

      if (mapped[OBEGEID]) mapped[OBEGEID].unshift(objekt);
      else mapped[OBEGEID] = [objekt];
    }
    mapped[-1] = objekte;

    return mapped;
  });

  protected readonly zugeordneteObjekte = computed<Table>(() => ({
    rows: this._zuordnungMap()[this.obegeid()] ?? [],
    columns: this.allObjekte().columns,
  }));
  protected readonly selectedZuordnungen = linkedSignal<Row[]>(() => (this.obegeid(), []));

  protected readonly objekte = computed<Table>(() => ({
    rows: this._zuordnungMap()[-1] ?? [],
    columns: this.allObjekte().columns,
  }));
  protected readonly selectedObjekte = linkedSignal<Row[]>(() => (this.objekte(), []));
  //#endregion

  protected readonly isInsertNeu = signal(false);
  protected readonly isAddObjekteModalOpen = signal(false);

  protected readonly groups = toSignal<Gruppe[], Gruppe[]>(this.apiService.getGruppen(), { initialValue: [] });
  protected readonly selectedGroups = signal<Gruppe[]>([]);

  protected readonly filteredAlleObjekte = computed(() => this.filterStore.filteredRows());
  protected readonly filteredZugeordneteObjekte = computed(() => this.filterStore_II.filteredRows());

  constructor() {
    this.filterStore.registerTable(this.objekte);
    this.filterStore.registerFilters(this.columnFilters);
    this.filterStore_II.registerTable(this.zugeordneteObjekte);
    this.filterStore_II.registerFilters(this.columnFilters_II);

    effect(() => {
      const selected = this.selectedGroups();
      this.formGroup.get('Gruppen').setValue(selected.map(gr => gr.id).join(';'));
    });

    this.formGroup
      .get('Gruppen')
      .valueChanges.pipe(pairwise(), takeUntilDestroyed())
      .subscribe(([oldGruppen, gruppen]) => {
        if (!gruppen || oldGruppen == gruppen) return;

        const IDs = gruppen.split(';');
        const selected = this.groups().filter(gr => IDs.includes(gr.id.toString()));
        this.selectedGroups.set(selected);
      });

    effect(() => {
      if (this.isInsertNeu()) {
        this.formGroup.reset();
        this.formTab().tabLink.activate();
        this.selectedBegehung.set(null);
      }
    });

    effect(() => {
      const selected = this.selectedBegehung();
      if (selected) {
        this.isInsertNeu.set(false);
        this.formGroup.patchValue(selected);
      }
    });

    effect(() => {
      if (this.selectedBegehung() || this.isInsertNeu()) this.formGroup.enable();
      else this.formGroup.disable();
    });
  }

  /**
   * @method erstellt oder bearbeitet BegehungsEinstellungen
   * @reloads begehungsTable
   */
  protected async saveItem() {
    try {
      const id = await firstValueFrom(this.apiService.setBegehungsEinstellungen(this.formGroup.getRawValue()));
      this.tableResource.reload();
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
      this.dataRefresh.next();
      this.selectedBegehung.set({ ID: id } as BegehungsEinstellungen);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  /**
   * @method löscht BegehungsEinstellungen und alle ihre Zuordnungen
   * @reloads begehungsTable u. Zuordnungen
   */
  protected async deleteBegehung() {
    const text = this.mrTranslate.transform('Eintrag wirklich löschen?');
    const ok = await this.confirm.danger(text);
    if (!ok) return;

    try {
      await firstValueFrom(this.apiService.deleteBegehungsEinstellungen(this.obegeid()));
      this.tableResource.reload();
      this.zuordnungenResource.reload();
      this.selectedBegehung.set(null);
      this.toastr.info(this.mrTranslate.transform('Eintrag wurde gelöscht'));
      this.dataRefresh.next();
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  /**
   * @method inserts Objekt-Zuordnungen zur ausgewählten Begehung
   * @reloads Zuordnungen
   */
  protected async addObjektZuordnungen() {
    const OSTAMMIDIds = this.selectedObjekte().map(items => +items.OSTAMMID);

    try {
      await firstValueFrom(this.apiService.addZuordnungen(this.obegeid(), OSTAMMIDIds));
      this.selectedZuordnungen.set([...this.selectedObjekte()]);
      this.zuordnungenResource.reload();
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'));
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  /**
   * @method löscht Objekt-Zuordnungen von der ausgewählten Begehung
   * @reloads Zuordnungen
   */
  protected async deleteObjekteZuordnungen() {
    const text = this.mrTranslate.transform('Einträge wirklich löschen?');
    const ok = await this.confirm.danger(text);
    if (!ok) return;

    const OSTAMMIDIds = this.selectedZuordnungen().map(items => +items.OSTAMMID);
    try {
      await firstValueFrom(this.apiService.deleteZuordnungen(this.obegeid(), OSTAMMIDIds));
      this.zuordnungenResource.reload();
      this.selectedZuordnungen.set([]);
      this.toastr.info(this.mrTranslate.transform('Einträge wurde gelöscht'));
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Etwas ist schief gelaufen'));
    }
  }

  public close() {
    this.dataRefresh?.next();
    this.closedialog.next(true);
  }
}

export interface BegehungZuordnung {
  ID: number;
  OBEGEID: number;
  OSTAMMID: number;
}

export interface BegehungsEinstellungen {
  ID: number;
  Begehungsart: string;
  Beginn: string /* Date */;
  Intervall: number;
  Tag: 1 | 2 | 3 | 4 | 5 | 6 | 7 /* Wochentag */;
  Beschreibung: string;
  Gruppen: string /* IDs joined ; */;
}
